import { useState } from "react";
import { Avatar } from "@heroui/react";
import {
  ConfirmModal,
  CustomModal,
  StaticSelect,
  Button,
  Spinner,
  Badge,
  Card,
  showToast,
} from "../shared";
import {
  useUpdateTeamMemberRole,
  useUpdateTeamMemberAccess,
  useRemoveTeamMember,
} from "../../lib/react-query/teams";

const ROLE_OPTIONS = [
  { id: "ADMIN", name: "Admin" },
  { id: "MEMBER", name: "Member" },
];

const ROLE_DESCRIPTIONS = {
  ADMIN: "Can manage members, invitations, settings, and shared files.",
  MEMBER: "Can work with projects and files allowed by team access.",
};

const ROLE_BADGE_COLOR = {
  OWNER: "warning",
  ADMIN: "primary",
  MEMBER: "neutral",
};

const StatBox = ({ label, value, borderColor }) => (
  <div className={`border rounded-lg p-3 border-l-2 bg-white ${borderColor}`}>
    <p className="text-xs text-grey">{label}</p>
    <p className="text-xl font-semibold text-darkgrey">{value}</p>
  </div>
);

const TeamMembersSection = ({ teamId, team, members, canManageTeam, currentUserId, projects = [] }) => {
  const { mutateAsync: updateRole } = useUpdateTeamMemberRole();
  const { mutateAsync: updateAccess } = useUpdateTeamMemberAccess();
  const { mutateAsync: removeMember } = useRemoveTeamMember();

  const [activeUserId, setActiveUserId] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [accessMember, setAccessMember] = useState(null);
  const [accessDraft, setAccessDraft] = useState({
    projectRole: "COLLABORATOR",
    allProjectsAccess: true,
    projectIds: [],
  });
  const selectableProjects = projects.filter((project) => project.visibility !== "RESTRICTED");

  const isOwner = (member) => member?.user?.id === team?.owner;
  const isSelf = (member) => member?.user?.id === currentUserId;

  const handleRoleChange = async (userId, newRole) => {
    try {
      setActiveUserId(userId);
      await updateRole({ teamId, userId, payload: { role: newRole } });
      showToast("Role updated", "success", { position: "top-center" });
    } catch {
      showToast("Failed to update role", "error", { position: "top-center" });
    } finally {
      setActiveUserId(null);
    }
  };

  const openAccessModal = (member) => {
    setAccessMember(member);
    setAccessDraft({
      projectRole: member.project_role || "COLLABORATOR",
      allProjectsAccess: member.all_projects_access ?? true,
      projectIds: Array.isArray(member.project_ids) ? member.project_ids : [],
    });
  };

  const toggleProject = (projectId) => {
    setAccessDraft((current) => ({
      ...current,
      projectIds: current.projectIds.includes(projectId)
        ? current.projectIds.filter((id) => id !== projectId)
        : [...current.projectIds, projectId],
    }));
  };

  const saveProjectAccess = async () => {
    if (!accessMember) return;
    if (!accessDraft.allProjectsAccess && accessDraft.projectIds.length === 0) {
      showToast("Select at least one project", "error", { position: "top-center" });
      return;
    }

    try {
      setActiveUserId(accessMember.user.id);
      await updateAccess({
        teamId,
        userId: accessMember.user.id,
        payload: {
          project_role: accessDraft.projectRole,
          all_projects_access: accessDraft.allProjectsAccess,
          project_ids: accessDraft.allProjectsAccess ? [] : accessDraft.projectIds,
        },
      });
      showToast("Project access updated", "success", { position: "top-center" });
      setAccessMember(null);
    } catch {
      showToast("Failed to update project access", "error", { position: "top-center" });
    } finally {
      setActiveUserId(null);
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    try {
      setRemovingUserId(confirmRemove);
      await removeMember({ teamId, userId: confirmRemove });
      showToast("Member removed", "success", { position: "top-center" });
      setConfirmRemove(null);
    } catch {
      showToast("Failed to remove member", "error", { position: "top-center" });
    } finally {
      setRemovingUserId(null);
    }
  };

  if (!Array.isArray(members) || members.length === 0) {
    return (
      <Card variant="outlined" className="border-2 border-dashed border-primary/15 p-8 text-center">
        <p className="text-lg font-medium text-primary mb-1">No members yet</p>
        <p className="text-sm text-mediumgrey max-w-sm mx-auto">
          Invite people to join this team.
        </p>
      </Card>
    );
  }

  const adminCount = members.filter((m) => m.role === "ADMIN" || isOwner(m)).length;
  const memberCount = members.filter((m) => m.role === "MEMBER").length;

  return (
    <div className="space-y-5">
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatBox label="Total members" value={members.length} borderColor="border-l-secondary/60" />
          <StatBox label="Admins" value={adminCount} borderColor="border-l-primary/40" />
          <StatBox label="Members" value={memberCount} borderColor="border-l-warning/60" />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border bg-surface-hover/50">
          <h2 className="text-base font-semibold text-darkgrey">Members</h2>
          <span className="text-xs text-mediumgrey">
            {members.length} {members.length === 1 ? "member" : "members"}
          </span>
        </div>

        <div className="divide-y divide-surface-border">
          {members.map((member) => {
            const user = member?.user || {};
            const isOwnerMember = isOwner(member);
            const isLoading = activeUserId === user.id;
            const isSelfMember = isSelf(member);
            const isRemoving = removingUserId === user.id;

            return (
              <div
                key={user.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5 hover:bg-surface-hover/30 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Avatar
                    src={user.profile_image || undefined}
                    name={(user.full_name || user.email || "?")[0]?.toUpperCase()}
                    className="!size-9 shrink-0"
                    classNames={{
                      base: "bg-primary/10 text-primary",
                    }}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-darkgrey truncate">
                      {user.full_name || "Unnamed"}
                      {isSelfMember && (
                        <span className="ml-2 text-[10px] font-normal text-mediumgrey uppercase tracking-wide">You</span>
                      )}
                    </p>
                    <p className="text-xs text-grey truncate">{user.email || ""}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 sm:justify-end">
                  {isOwnerMember ? (
                    <Badge color="warning" size="sm" className="uppercase tracking-wide">Owner</Badge>
                  ) : canManageTeam ? (
                    <>
                      <div className="w-28">
                        <StaticSelect
                          label=""
                          options={ROLE_OPTIONS}
                          value={member.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={isLoading || isSelfMember}
                          size="sm"
                          placeholder="Role"
                        />
                        <p className="mt-1 text-[11px] leading-4 text-mediumgrey">
                          {ROLE_DESCRIPTIONS[member.role]}
                        </p>
                      </div>

                      <span className="text-xs text-mediumgrey whitespace-nowrap">
                        {member.all_projects_access ? "All projects" : "Selected projects"}
                      </span>

                      <Button
                        className="min-w-0 px-2"
                        variant="transparent"
                        color="secondary"
                        size="sm"
                        onClick={() => openAccessModal(member)}
                        disabled={isLoading || isSelfMember}
                      >
                        Manage access
                      </Button>

                      {canManageTeam && !isOwnerMember && !isSelfMember && (
                        <Button
                          className="min-w-0 px-2"
                          variant="transparent"
                          color="danger"
                          size="sm"
                          onClick={() => setConfirmRemove(user.id)}
                          disabled={isRemoving}
                        >
                          {isRemoving ? <Spinner width={14} height={14} /> : "Remove"}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Badge color={ROLE_BADGE_COLOR[member.role] || "neutral"} size="sm" className="uppercase tracking-wide">
                        {member.role || "MEMBER"}
                      </Badge>
                      <span className="text-xs text-mediumgrey whitespace-nowrap">
                        {member.all_projects_access ? "All projects" : "Limited access"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <ConfirmModal
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={handleRemove}
        title="Remove member"
        message="Are you sure you want to remove this member from the team? They will lose access to all team resources."
        confirmText="Remove"
        confirmColor="danger"
        isLoading={!!removingUserId}
      />

      <CustomModal
        title="Manage project access"
        open={!!accessMember}
        close={() => setAccessMember(null)}
        primaryButtonText="Save access"
        primaryButtonClick={saveProjectAccess}
        primaryButtonLoading={!!accessMember && activeUserId === accessMember.user.id}
        secondaryButtonText="Cancel"
        secondaryButtonClick={() => setAccessMember(null)}
      >
        <div className="space-y-4">
          <StaticSelect
            label="Project role"
            options={[
              { id: "COLLABORATOR", name: "Collaborator" },
              { id: "MANAGER", name: "Manager" },
            ]}
            value={accessDraft.projectRole}
            onChange={(event) => setAccessDraft((current) => ({ ...current, projectRole: event.target.value }))}
          />
          <StaticSelect
            label="Project access"
            options={[
              { id: "ALL", name: "All projects" },
              { id: "SELECTED", name: "Selected projects" },
            ]}
            value={accessDraft.allProjectsAccess ? "ALL" : "SELECTED"}
            onChange={(event) => setAccessDraft((current) => ({
              ...current,
              allProjectsAccess: event.target.value === "ALL",
            }))}
          />
          {!accessDraft.allProjectsAccess && (
            <div className="rounded-lg border border-surface-border p-3">
              <p className="mb-2 text-sm font-medium text-darkgrey">Choose projects</p>
              <div className="space-y-2">
                {selectableProjects.length > 0 ? selectableProjects.map((project) => (
                  <label key={project.id} className="flex items-center gap-2 text-sm text-darkgrey">
                    <input
                      type="checkbox"
                      checked={accessDraft.projectIds.includes(project.id)}
                      onChange={() => toggleProject(project.id)}
                    />
                    <span>{project.title || project.name || "Untitled project"}</span>
                  </label>
                )) : (
                  <p className="text-sm text-mediumgrey">No team projects are available.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default TeamMembersSection;
